import * as Yup from 'yup'
import isEmpty from 'lodash/isEmpty'
import rollbar from '@mortgage-pos/ui/services/rollbar'
import { shouldShowQuestion } from './visibleIf'
import interpolateAnswerData from './interpolateAnswerData'
import { autoCompleteAddressQuestions } from '@mortgage-pos/data'
import { Questions, QuestionGroup, Page as PageType } from '@mortgage-pos/types'

import {
  calculateDefaultValue,
  mapAddressGroupValues,
  componentIsQuestionGroup,
} from './questionHelpers'

import {
  createYupSchema,
  createObjectArraySchema,
} from './createValidationSchema'

import {
  CustomPageValidation,
  customPageValidations,
  validatePageCondition,
} from './customPageValidations'

type FormikErrorsMap = Record<string, string>

export const buildInitialValues = (questions: Questions[], answers) =>
  questions.reduce((acc, question) => {
    if (!answers) {
      return acc
    }

    if (question.component === 'Question-AddressFieldsAutoComplete') {
      const addressFields = [
        'autoCompleteAddressFields',
        'street',
        'apt',
        'city',
        'state',
        'zipCode',
      ]

      addressFields.forEach((field) => {
        acc[field] = answers[field] ?? ''
      })

      return acc
    }

    const questionName: string = question.questionName.name

    if (answers[questionName] != null) {
      const existingAnswer = answers[questionName]

      const isEmptyGroupAnswer =
        componentIsQuestionGroup(question.component) &&
        isEmptyArray(existingAnswer)

      acc[questionName] = isEmptyGroupAnswer
        ? calculateDefaultValue(question) // Fill in object shape if empty
        : existingAnswer

      return acc
    }

    if (
      componentIsQuestionGroup(question.component) &&
      questionName.includes('+')
    ) {
      const [relatedGroupName, index] = questionName.split('+')
      const groupAnswers = answers[relatedGroupName]

      if (!groupAnswers) {
        acc[questionName] = calculateDefaultValue(question)
        return acc
      }

      const isAddressesGroup = ['addresses', 'coBorrowerAddresses'].includes(
        relatedGroupName
      )

      if (isAddressesGroup) {
        // This is the key that indicates if the borrower lives at the main
        // address. The key differs for primary vs co-borrower
        const livesAtKey =
          relatedGroupName === 'addresses'
            ? 'isCurrentAddress'
            : 'doesCoBorrowerHaveSameAddress'

        acc[questionName] = mapAddressGroupValues(question, answers, livesAtKey)
        return acc
      }

      acc[questionName] = [answers[relatedGroupName][index]]
      return acc
    }

    acc[questionName] = calculateDefaultValue(question)

    return acc
  }, {})

export const generateDynamicYupSchema = (
  questions,
  formValues,
  prebuildValidations
) => {
  const schema = questions.reduce((schema, question) => {
    const { component, validations } = question
    const questionName: string = question.questionName.name

    const visibleIfCondition = question.visibleIfConstructor?.[0]

    if (
      !shouldShowQuestion(formValues, visibleIfCondition, prebuildValidations)
    ) {
      return schema
    }

    if (component === 'Question-AddressFieldsAutoComplete') {
      autoCompleteAddressQuestions.forEach((question) => {
        const { questionName, validations } = question
        schema[questionName] = createYupSchema(validations[0])
      })

      return schema
    }

    if (componentIsQuestionGroup(component)) {
      const { questions } = question as QuestionGroup

      schema[questionName] = createObjectArraySchema(questions)
      return schema
    }

    if (!validations || validations.length === 0) {
      return schema
    }

    schema[questionName] = createYupSchema(validations[0])
    return schema
  }, {})

  return Yup.object(schema)
}

export const interpolateAnswersInPage = (
  page: PageType,
  answers: object
): PageType => {
  const title = interpolateAnswerData(page.title, answers)
  const subTitle = interpolateAnswerData(page.subTitle, answers)
  const description = interpolateAnswerData(page.description, answers)
  const questions = page.questions?.map((question) => {
    // @ts-ignore
    // ts doesn't like the circular reference in the types
    let subQuestions = question.questions
    if (subQuestions?.length > 0) {
      // for nested question groups, use recursion to interpolate text
      subQuestions = subQuestions?.map((subQuestion) => {
        return interpolateAnswersInPage(subQuestion, answers)
      })
    }

    const options =
      'options' in question
        ? question.options.map((option) => ({
            ...option,
            text: interpolateAnswerData(option.text, answers),
          }))
        : []
    return {
      ...question,
      title: interpolateAnswerData(question.title, answers),
      subTitle: interpolateAnswerData(question.subTitle, answers),
      options,
      questions: subQuestions,
    }
  })

  return {
    ...page,
    title,
    subTitle,
    description,
    questions,
  }
}

export const deriveAnswersFromFormValues = (
  questions,
  formValues,
  prebuiltValidations
) => {
  // Remove any default answers to questions that are not visible
  const answersToVisibleQuestions = getAnswersToVisibleQuestions(
    questions,
    formValues,
    prebuiltValidations
  )

  return collectRelatedQuestionGroupArrays(answersToVisibleQuestions)
}

export const getAnswersToVisibleQuestions = (
  questions,
  formValues,
  prebuiltValidations
) =>
  questions.reduce((answers, question) => {
    const { component } = question
    const questionName: string = question.questionName.name

    const visibleIfCondition = question.visibleIfConstructor?.[0]

    if (
      !shouldShowQuestion(formValues, visibleIfCondition, prebuiltValidations)
    ) {
      return answers
    }

    const questionValue = formValues[questionName]

    // For question group components, the value is an array of objects
    // Map through objects and recursively get answers to visible questions within
    if (componentIsQuestionGroup(component)) {
      const { questions } = question as QuestionGroup
      // if the question is of type autocomplete...map/recurse that sucker and add in the xtras

      answers[questionName] = questionValue.map((groupAnswers) =>
        getAnswersToVisibleQuestions(
          questions,
          groupAnswers,
          prebuiltValidations
        )
      )

      return answers
    }

    answers[questionName] = questionValue
    return answers
  }, {})

// This function handles answer properties, like "addresses+0" or "addresses+2"
// It converts them to an array of objects and adds them to the final answers
const collectRelatedQuestionGroupArrays = (answers) =>
  Object.entries(answers).reduce((finalAnswers, [key, value]) => {
    if (Array.isArray(value) && key.includes('+')) {
      // Whatever comes after the "+" doesn't really matter, we're just using it to have a unique question name
      // The question group defines what type of address it is, ex. "current" or "previous"
      const [questionName] = key.split('+')

      finalAnswers[questionName] = finalAnswers[questionName]
        ? [...finalAnswers[questionName], ...value]
        : value

      return finalAnswers
    }

    finalAnswers[key] = value
    return finalAnswers
  }, {})

export const calculateSumOfFields = (answers: object) =>
  Object.values(answers).reduce((sum, answer) => {
    if (typeof answer === 'number') {
      sum += answer
    }
    return sum
  }, 0)

export const getCustomValidator =
  (customPageValidationKey: string, customPageValidationErrorMessage: string) =>
  async (
    answers?: object,
    values?: object,
    formikUtils?: object
  ): Promise<FormikErrorsMap> => {
    const pageCondition = customPageValidationKey as CustomPageValidation
    const errors: FormikErrorsMap = {}

    if (!pageCondition) {
      return errors
    }

    if (!customPageValidations.includes(pageCondition)) {
      rollbar.error(`No custom validation logic for condition ${pageCondition}`)
      return errors
    }

    try {
      await validatePageCondition[pageCondition](
        {
          answers,
          values,
        },
        formikUtils
      )
      return {}
    } catch (errorMessage) {
      return isEmpty(errorMessage)
        ? { page: customPageValidationErrorMessage }
        : errorMessage
    }
  }

function isEmptyArray(input: any) {
  return Array.isArray(input) && input.length <= 0
}
